import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff, FiUser } from 'react-icons/fi';
import { useAuth } from '../AuthContext';
import {
  clearRememberedLogin,
  readRememberedLogin,
  storeRememberedLogin,
} from '../security/rememberedLogin';

const WELCOME_LOADER_KEY = 'bpm-show-welcome-loader';

const formatRetryMessage = (error) => {
  const base = error?.message || 'Falha no login';
  const seconds = Number(error?.retryAfter);
  if (!Number.isFinite(seconds) || seconds <= 0) return base;
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `${base} Tente novamente em aproximadamente ${minutes} minuto(s).`;
};

export default function Login() {
  const [initialLogin] = useState(() => {
    if (typeof window === 'undefined') return '';
    return readRememberedLogin(window.localStorage);
  });
  const [username, setUsername] = useState(() => initialLogin);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [rememberCredentials, setRememberCredentials] = useState(
    () => Boolean(initialLogin),
  );
  const [showPassword, setShowPassword] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login, completeMfa } = useAuth();

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previousTheme = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');

    return () => {
      if (previousTheme) {
        document.documentElement.setAttribute('data-theme', previousTheme);
      } else {
        document.documentElement.removeAttribute('data-theme');
      }
    };
  }, []);

  const handleToggleRemember = () => {
    setRememberCredentials((prev) => {
      const next = !prev;
      if (typeof window === 'undefined') return next;
      if (!next) clearRememberedLogin(window.localStorage);
      return next;
    });
  };

  const handlePointerMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    event.currentTarget.style.setProperty('--access-glow-x', `${x}%`);
    event.currentTarget.style.setProperty('--access-glow-y', `${y}%`);
  };

  const enterDashboard = () => {
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(WELCOME_LOADER_KEY, '1');
      } catch {
        // ignore
      }
    }
    navigate('/dashboard');
  };

  const persistRememberedLogin = (username) => {
    if (typeof window === 'undefined') return;
    if (rememberCredentials) storeRememberedLogin(window.localStorage, username);
    else clearRememberedLogin(window.localStorage);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    const normalizedUsername = username.trim();
    if (!normalizedUsername || !password) {
      setError('Informe o usuário e a senha.');
      setSubmitting(false);
      return;
    }

    try {
      const result = await login(normalizedUsername, password);
      persistRememberedLogin(normalizedUsername);

      if (result?.mfaRequired === true) {
        setMfaChallenge(result);
        setMfaCode('');
        return;
      }
      enterDashboard();
    } catch (err) {
      setError(formatRetryMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfaSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    const code = mfaCode.trim();
    if (!code) {
      setError('Informe o código do autenticador ou um código de recuperação.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await completeMfa({
        challengeToken: mfaChallenge?.challengeToken,
        code,
      });
      const oneTimeCodes = Array.isArray(result?.recoveryCodes)
        ? result.recoveryCodes
        : [];
      if (oneTimeCodes.length > 0) {
        setRecoveryCodes(oneTimeCodes);
        setMfaChallenge(null);
        setMfaCode('');
      } else {
        enterDashboard();
      }
    } catch (err) {
      setError(err?.message || 'Não foi possível validar o código.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetMfa = () => {
    setMfaChallenge(null);
    setMfaCode('');
    setError(null);
  };

  const cardContent = recoveryCodes.length > 0 ? (
    <div className="space-y-5">
      <div className="text-center">
        <h1 className="text-xl font-semibold text-slate-900">Códigos de recuperação</h1>
        <p className="mt-2 text-sm text-slate-600">
          Guarde estes códigos em um local seguro. Cada código pode ser usado apenas uma vez e não será exibido novamente.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-950 p-4 font-mono text-sm text-white">
        {recoveryCodes.map((code) => (
          <code key={code} className="rounded bg-white/10 px-2 py-1 text-center">
            {code}
          </code>
        ))}
      </div>
      <button
        type="button"
        onClick={enterDashboard}
        className="w-full rounded-xl bg-gradient-to-r from-blue-700 to-blue-400 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-lg"
      >
        Já guardei os códigos
      </button>
    </div>
  ) : mfaChallenge ? (
    <form onSubmit={handleMfaSubmit} className="space-y-5">
      <div className="text-center">
        <h1 className="text-xl font-semibold text-slate-900">
          {mfaChallenge.setupRequired
            ? 'Configurar autenticação em duas etapas'
            : 'Confirmar autenticação em duas etapas'}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {mfaChallenge.setupRequired
            ? 'Adicione a conta ao seu aplicativo autenticador e informe o código de seis dígitos.'
            : 'Informe o código atual do aplicativo autenticador. Um código de recuperação também é aceito.'}
        </p>
      </div>

      {mfaChallenge.setupRequired && mfaChallenge.secret && (
        <div className="space-y-2 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-950">
          <p className="font-semibold">Chave manual do autenticador</p>
          <code className="block break-all rounded-lg bg-white px-3 py-2 font-mono font-semibold tracking-wider shadow-sm">
            {mfaChallenge.secret}
          </code>
          <p className="text-xs text-blue-700">
            Tipo TOTP, seis dígitos, período de 30 segundos. O QR pode ser gerado pelo próprio aplicativo a partir do endereço otpauth fornecido.
          </p>
        </div>
      )}

      {error && <p className="text-center text-sm font-medium text-red-500">{error}</p>}
      <div>
        <label htmlFor="mfa-code" className="mb-1 block text-sm font-semibold text-slate-700">
          Código do autenticador
        </label>
        <input
          id="mfa-code"
          name="one-time-code"
          type="text"
          value={mfaCode}
          onChange={(event) => setMfaCode(event.target.value)}
          autoComplete="one-time-code"
          inputMode="numeric"
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-center font-mono text-lg tracking-[0.35em] text-slate-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
          placeholder="000000"
          required
          autoFocus
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded-xl bg-gradient-to-r from-blue-700 to-blue-400 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-lg disabled:opacity-60"
      >
        {submitting ? 'Validando...' : 'Validar código'}
      </button>
      <button
        type="button"
        onClick={resetMfa}
        disabled={submitting}
        className="w-full rounded-xl border border-slate-200 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
      >
        Voltar ao login
      </button>
    </form>
  ) : (
    <form
      autoComplete="off"
      data-form-type="other"
      onSubmit={handleSubmit}
    >
      <div className="mb-6 flex flex-col items-center text-center">
        <h1 className="text-base font-light tracking-[0.12em] text-slate-900">
          Intelligent BPM Nash Tecnologia
        </h1>
        <p className="mt-2 text-sm text-slate-500">Acesse a plataforma com suas credenciais</p>
      </div>
      {error && <p className="mb-4 text-center text-sm font-medium text-red-500">{error}</p>}
      <div className="mb-4">
        <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="login">
          E-mail ou CPF
        </label>
        <input
          id="login"
          name="login-identifier"
          type="text"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
          placeholder="seu.email@nash.com.br ou 000.000.000-00"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          inputMode="text"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-form-type="other"
          required
        />
      </div>
      <div className="mb-6">
        <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="password">
          Senha
        </label>
        <div className="relative">
          <input
            id="password"
            name="login-secret"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-12 text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
            placeholder="Digite sua senha"
            autoComplete="new-password"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-slate-500 transition hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            aria-pressed={showPassword}
          >
            {showPassword ? <FiEyeOff aria-hidden /> : <FiEye aria-hidden />}
          </button>
        </div>
      </div>
      <div className="mb-8 flex items-center justify-between text-xs font-medium uppercase tracking-wide text-slate-500">
        <button
          type="button"
          onClick={handleToggleRemember}
          aria-pressed={rememberCredentials}
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 transition focus:outline-none focus:ring-2 focus:ring-blue-400/60 ${
            rememberCredentials
              ? 'border-blue-400 bg-blue-50 text-blue-600'
              : 'border-slate-200 bg-white text-slate-500 hover:border-blue-200 hover:text-blue-600'
          }`}
        >
          <span
            className={`h-2.5 w-2.5 rounded-full ${
              rememberCredentials ? 'bg-blue-500' : 'bg-slate-300'
            }`}
          />
          Lembrar usuário
        </button>
      </div>
      <button
        className="w-full rounded-xl bg-gradient-to-r from-blue-700 to-blue-400 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-lg transition hover:from-blue-600 hover:to-blue-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400/60 focus:ring-offset-white disabled:opacity-60"
        type="submit"
        disabled={submitting}
      >
        {submitting ? 'Entrando...' : 'Entrar'}
      </button>
      <div className="mt-10 flex justify-center">
        <img
          src="/nashlogoazul.png"
          alt="Logo da Nash"
          className="h-24 w-24 drop-shadow-xl"
        />
      </div>
    </form>
  );

  return (
    <div
      className="relative min-h-screen overflow-hidden flex flex-col items-center justify-center gap-6 bg-slate-950 bg-gradient-to-br from-black via-[#050b1a] to-[#08204a] p-4"
      onPointerMove={handlePointerMove}
      style={{
        '--access-glow-x': '50%',
        '--access-glow-y': '50%',
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 transition-opacity duration-300"
        style={{
          background:
            'radial-gradient(circle 260px at var(--access-glow-x) var(--access-glow-y), rgba(59, 130, 246, 0.36), rgba(14, 165, 233, 0.16) 34%, transparent 68%)',
        }}
      />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(37,99,235,0.16),transparent_36%)]" />
      <div className="relative z-10 flex flex-col items-center gap-4 text-center text-white">
        <div className="flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-full bg-white shadow-xl">
          <FiUser aria-hidden className="text-[2.5rem] text-[#001133]" />
        </div>
        <span className="text-[2.25rem] font-semibold tracking-[0.12em] text-white">
          Área de Acesso
        </span>
      </div>
      <div className="relative z-10 w-full max-w-sm rounded-3xl border border-white/10 bg-white/95 p-6 shadow-2xl backdrop-blur">
        {cardContent}
      </div>
    </div>
  );
}
